import React, { useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import './Services.css';
import Reveal from '../shared/Reveal';
import SampleTag from '../shared/SampleTag';
import { buildZoneRows } from '../shared/zonesData';
import { CHART_COLORS, CHART_FONT, chartAnimation, secondsToLabel } from '../shared/chartSetup';
import tilePhoto from '../Hero/assets/HeroImage3.jpg';
import { LuChartBarBig, LuCalendarDays, LuLaptop, LuChartLine } from 'react-icons/lu';

const WEEK = [
    { day: 'Mon', label: 'Aerobic Training' },
    { day: 'Tue', label: 'Lactate Threshold Training' },
    { day: 'Thu', label: 'VO2max Training' },
    { day: 'Sat', label: 'Race Warm Up' }
];

function ZoneChart() {
    const { data, options } = useMemo(() => {
        const zones = buildZoneRows(100).filter(row => !row.isOpenEnded);
        return {
            data: {
                labels: zones.map(zone => zone.title),
                datasets: [
                    {
                        data: zones.map(zone => [zone.to, zone.from]),
                        backgroundColor: CHART_COLORS.channel,
                        borderRadius: 2,
                        borderSkipped: false,
                        barPercentage: 0.62
                    }
                ]
            },
            options: {
                indexAxis: 'y',
                responsive: true,
                maintainAspectRatio: false,
                animation: chartAnimation(),
                plugins: {
                    legend: { display: false },
                    tooltip: {
                        callbacks: {
                            label: ctx =>
                                `${secondsToLabel(ctx.raw[0], 2)} to ${secondsToLabel(ctx.raw[1], 2)} per 100m`
                        }
                    }
                },
                scales: {
                    x: {
                        min: 75,
                        max: 90,
                        reverse: true,
                        grid: { color: 'rgba(8, 31, 92, 0.14)' },
                        ticks: {
                            color: CHART_COLORS.soft,
                            font: CHART_FONT,
                            callback: value => secondsToLabel(value)
                        }
                    },
                    y: {
                        grid: { display: false },
                        ticks: { color: CHART_COLORS.deep, font: { ...CHART_FONT, size: 16 } }
                    }
                }
            }
        };
    }, []);

    return <Bar data={data} options={options} />;
}

export default function Services() {
    return (
        <section id='services' className='lp-section lp-services'>
            <div className='lp-wrap'>
                <Reveal>
                    <p className='lp-eyebrow'>What it does</p>
                    <h2 className='lp-h2'>One platform for the test, the plan and the race.</h2>
                </Reveal>

                <div className='lp-bento'>
                    <Reveal className='lp-tile lp-tile-chart' delay={0}>
                        <LuChartBarBig className='lp-tile-icon' aria-hidden='true' />
                        <h3>Advanced analytics</h3>
                        <p>
                            Charts built from the athlete&apos;s own test. Here are the End 1 to End 3
                            pace bands for one CSS, per 100m.
                        </p>
                        <div className='lp-tile-chartbox' role='img' aria-label='Bar chart of End 1, End 2 and End 3 pace ranges per 100 metres for a CSS of 1:20'>
                            <ZoneChart />
                        </div>
                        <div className='lp-tile-note'>
                            <SampleTag>Example</SampleTag>
                            <span>CSS 1:20.00 / 100m, pace axis runs fast on the right.</span>
                        </div>
                    </Reveal>

                    <Reveal className='lp-tile lp-tile-photo' delay={100}>
                        <img src={tilePhoto} alt='' loading='lazy' />
                        <div className='lp-tile-photo-text'>
                            <LuLaptop className='lp-tile-icon' aria-hidden='true' />
                            <h3>Access from anywhere</h3>
                            <p>It runs in the browser. Log results from the deck, review them at home.</p>
                        </div>
                    </Reveal>

                    <Reveal className='lp-tile lp-tile-plan' delay={200}>
                        <LuCalendarDays className='lp-tile-icon' aria-hidden='true' />
                        <h3>Training plan organization</h3>
                        <p>Workouts and races live in one calendar view for the athlete.</p>
                        <ul className='lp-week'>
                            {WEEK.map(item => (
                                <li key={item.day}>
                                    <span className='lp-week-day'>{item.day}</span>
                                    <span>{item.label}</span>
                                </li>
                            ))}
                        </ul>
                        <SampleTag>Example week</SampleTag>
                    </Reveal>

                    <Reveal className='lp-tile lp-tile-race' delay={300}>
                        <LuChartLine className='lp-tile-icon' aria-hidden='true' />
                        <h3>Race analysis</h3>
                        <p>
                            Review a race split by split, with stroke index, stroke rate and
                            stroke length charted beside the times.
                        </p>
                        <a href='#groups' className='lp-tile-link'>
                            See how coaches use it
                        </a>
                    </Reveal>
                </div>
            </div>
        </section>
    );
}
